// Самоизменение интерфейса (README §10.1–10.2): «+» и «✎» открывают беседу
// с агентом-сборщиком. Правила UI-кита (`GET /api/sections/uikit`) уходят ему
// системным промптом; адрес панели и токен сборщика (пускает ТОЛЬКО к черновику
// своего раздела и к UI-киту) сервер кладёт в окружение процесса —
// $AGENTBOX_URL / $AGENTBOX_TOKEN (`builderSection` в CreateSessionRequest).
// Без вопросов проходит только curl к /api/sections/<id>/… своей панели
// (server/agentbox/sessions/builder_perm.py). Правки идут в черновик;
// публикует оператор кнопкой «принять».

import { api } from '../../api'
import { useAppStore } from '../../store/appStore'
import type { Section } from '../../store/appStore'
import { describe } from './http'
import { useModels } from './model'
import { rememberChat, sectionsApi, serverId, useSections } from './store'
import type { SectionModel } from './types'

import { EDIT_HINT, NEW_HINT } from './hints'

export { EDIT_HINT, NEW_HINT }

function newId(): string {
  // [a-z][a-z0-9-]{1,39}: префикс «r-» + время в base36
  return `r-${Date.now().toString(36)}`
}

interface PromptInput {
  mode: 'new' | 'edit'
  id: string
  title: string
  rules: string
  example: SectionModel
  current?: SectionModel | null
  builtin?: boolean
  purpose?: string
}

export function composePrompt(p: PromptInput): string {
  const lines: string[] = []
  if (p.mode === 'new') {
    lines.push(
      'Ты собираешь новый раздел веб-панели agentbox. Раздел описывается одним JSON по правилам UI-кита ниже;',
      'панель рисует его сама общим рендерером и показывает оператору черновик вживую.',
    )
    if (p.purpose) lines.push('', p.purpose)
  } else {
    lines.push(
      `Ты правишь раздел «${p.title}» веб-панели agentbox. Правки идут в черновик — оператор видит его вживую`,
      'и сам решает: «принять» или «отменить».',
    )
    if (p.builtin) {
      lines.push(
        'Это встроенный раздел: черновик — оверлей поверх вшитого вида. Во вшитом виде панель применяет из оверлея:',
        'дополнительные разделы бокового меню (`sidebar`, под вшитым меню); кнопки `actions` с place:"panel" —',
        'строкой в шапке раздела; блоки `canvas.blocks` — над вшитым видом (или под ним, если у блока',
        '`props.place: "bottom"`), невысокой полосой со своей прокруткой — делай их компактными.',
        'В Беседах, Мессенджерах и Конструкторе блоки полотна во вшитый вид не встраиваются (рабочая область',
        'занята целиком) — там оверлей это меню и кнопки.',
      )
    }
  }
  lines.push(
    '',
    `id раздела: \`${p.id}\` — не меняй его, поле \`id\` в JSON должно совпадать.`,
    '',
    'Доступ к панели: адрес и токен сборщика уже в окружении — $AGENTBOX_URL и $AGENTBOX_TOKEN (токен открывает',
    'только черновик этого раздела). Без запроса прав у оператора проходит только один вызов curl на команду',
    `к $AGENTBOX_URL/api/sections/${p.id}/… — без цепочек (; && |), подстановок, перенаправлений и -o.`,
    'JSON черновика пиши файлом в рабочую папку (правка файла разрешена), потом отправляй его curl:',
    '',
    '```sh',
    `# прочитать черновик (404 — его ещё нет)`,
    `curl -sS "$AGENTBOX_URL/api/sections/${p.id}/draft" -H "X-Agentbox-Token: $AGENTBOX_TOKEN"`,
    `# записать черновик из файла .agentbox-section-${p.id}.json`,
    `curl -sS -X PUT "$AGENTBOX_URL/api/sections/${p.id}/draft" -H "X-Agentbox-Token: $AGENTBOX_TOKEN" -H 'Content-Type: application/json' --data @.agentbox-section-${p.id}.json`,
    '```',
    '',
    'Правила UI-кита и формат — ниже, запрашивать их у панели не нужно.',
    '',
  )
  if (p.current) {
    lines.push('Сейчас опубликовано:', '', '```json', JSON.stringify(p.current, null, 1), '```', '')
  }
  lines.push(p.rules.trim(), '', 'Пример раздела:', '', '```json', JSON.stringify({ ...p.example, id: p.id }, null, 1), '```', '')
  if (p.mode === 'new') {
    lines.push(
      'Оператор первой репликой опишет, каким должен быть раздел (подсказка у него в поле ввода:',
      `«${NEW_HINT}»). Если не ясно, что показывать, откуда данные и какие нужны действия, — коротко уточни.`,
      'Потом собери JSON, запиши черновик и скажи оператору, что можно смотреть вкладку черновика.',
    )
  } else {
    lines.push(
      `Оператор первой репликой скажет, что поменять (подсказка у него в поле ввода: «${EDIT_HINT}»).`,
      'Если просьба неясна — коротко уточни. Потом запиши черновик.',
    )
  }
  lines.push('Сам черновик не публикуй: `accept` вызывает только оператор.')
  return lines.join('\n')
}

/** Беседа со сборщиком: инструкции (UI-кит, формат) уходят системным промптом, а не
 *  репликой — в логе их нет, первая реплика за оператором. Режим прав — `acceptEdits`;
 *  сверх него без вопросов — только curl к API своего раздела (`builderSection`),
 *  остальные команды оператор подтверждает (не bypass, как у обычной беседы). */
async function openBuilderChat(title: string, section: string, systemPrompt: string): Promise<string> {
  const session = await api.createSession({
    type: 'agent', title, appendSystemPrompt: systemPrompt, permissionMode: 'acceptEdits', builderSection: section,
  })
  useAppStore.setState((s) => ({
    sessions: s.sessions.some((x) => x.id === session.id) ? s.sessions : [...s.sessions, session],
    paneIds: s.paneIds.includes(session.id) ? s.paneIds : [...s.paneIds, session.id],
    activeId: session.id,
    section: 'chats' as Section,
    mobSide: false,
  }))
  return session.id
}

const BUILTIN = ['chats', 'feed', 'flow', 'tracker', 'notes', 'messengers', 'library']

let busy = false

/** «+»: беседа «новый раздел» и вкладка «новый раздел · черновик» (§10.1). */
export async function startNewSection(opts: { purpose?: string } = {}): Promise<void> {
  if (busy) return
  busy = true
  const app = useAppStore.getState()
  try {
    const id = newId()
    const kit = await sectionsApi.uikit()
    const prompt = composePrompt({
      mode: 'new', id, title: 'новый раздел',
      rules: kit.prompt, example: kit.example, purpose: opts.purpose,
    })
    const sessionId = await openBuilderChat('новый раздел', id, prompt)
    rememberChat(id, sessionId)
    useAppStore.setState((s) => ({
      drafts: [...s.drafts, { id: `draft:${id}`, title: 'новый раздел', sessionId }],
      sessionHints: { ...s.sessionHints, [sessionId]: NEW_HINT },
    }))
  } catch (error) {
    app.setNotice?.(`новый раздел: ${describe(error)}`)
  } finally {
    busy = false
  }
}

/** «✎»: беседа «правка: <раздел>», правки — в черновик-оверлей (§10.2). */
export async function startEditSection(section: Section, title: string): Promise<void> {
  const app = useAppStore.getState()
  if (section.startsWith('draft:')) {
    const tab = app.drafts.find((d) => d.id === section)
    if (tab?.sessionId) app.focusSession(tab.sessionId)
    return
  }
  const existing = app.secEdit[section]
  if (existing && app.sessions.some((s) => s.id === existing && s.state !== 'exited')) {
    app.focusSession(existing)
    return
  }
  const id = serverId(section)
  if (!id) {
    app.setNotice?.(`раздел «${title}» не правится: он служебный`)
    return
  }
  if (busy) return
  busy = true
  try {
    const [kit, full] = await Promise.all([
      sectionsApi.uikit(),
      sectionsApi.full(id).catch(() => null),
    ])
    const builtin = kit.builtin.includes(id)
    const prompt = composePrompt({
      mode: 'edit', id, title, rules: kit.prompt, example: kit.example,
      current: full?.draft ?? full?.section ?? null, builtin,
    })
    const sessionId = await openBuilderChat(`правка: ${title}`, id, prompt)
    rememberChat(id, sessionId)
    useAppStore.setState((s) => ({
      secEdit: { ...s.secEdit, [section]: sessionId },
      sessionHints: { ...s.sessionHints, [sessionId]: EDIT_HINT },
    }))
  } catch (error) {
    app.setNotice?.(`правка раздела: ${describe(error)}`)
  } finally {
    busy = false
  }
}

/** Где живёт раздел в оболочке после публикации. */
export function shellSection(id: string): Section {
  return (BUILTIN.includes(id) ? id : `custom:${id}`) as Section
}

function dropDraftTab(id: string): void {
  useAppStore.setState((s) => {
    const secEdit = { ...s.secEdit }
    delete secEdit[shellSection(id)]
    return { drafts: s.drafts.filter((d) => d.id !== `draft:${id}`), secEdit }
  })
}

/** «принять»: черновик → опубликовано, метка «черновик» снимается. */
export async function acceptDraft(id: string): Promise<void> {
  await sectionsApi.accept(id)
  dropDraftTab(id)
  await Promise.all([useSections.getState().refresh(), useModels.getState().load(id, false)])
  useAppStore.getState().setSection(shellSection(id))
}

/** «отменить»: черновик удаляется; новый раздел без публикации исчезает. */
export async function discardDraft(id: string): Promise<void> {
  const summary = useSections.getState().list.find((s) => s.id === id)
  if (summary?.hasDraft) await sectionsApi.discard(id)
  dropDraftTab(id)
  void useModels.getState().load(id, true)
  rememberChat(id, null)
  await useSections.getState().refresh()
  const app = useAppStore.getState()
  const published = summary?.published || summary?.builtin
  if (app.section === `draft:${id}` || app.section === `custom:${id}`) {
    app.setSection(published ? shellSection(id) : 'chats')
  }
}

export async function rollbackTo(id: string, version: string): Promise<void> {
  await sectionsApi.rollback(id, version)
  await Promise.all([useSections.getState().refresh(), useModels.getState().load(id, false)])
}
